import React, { useEffect } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';

const PrivacyPolicyPage = () => {
    // Force page to start from top when component mounts
    useEffect(() => {
        // Scroll to top immediately (no smooth animation)
        window.scrollTo(0, 0);
        
        // Also set scroll restoration to manual to prevent browser from remembering position
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
    }, []);

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        
        mainContent: {
            flex: 1,
            backgroundColor: '#ffffff',
            padding: '3rem 0'
        },

        container: {
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0 2rem',
            lineHeight: '1.6'
        },

        pageTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '3rem',
            textAlign: 'center',
            borderBottom: '3px solid #22c55e',
            paddingBottom: '1rem'
        },

        sectionTitle: {
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#1a202c',
            marginTop: '2.5rem',
            marginBottom: '1rem'
        },

        firstSectionTitle: {
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#1a202c',
            marginTop: '0',
            marginBottom: '1rem'
        },

        paragraph: {
            fontSize: '1rem',
            color: '#4a5568',
            lineHeight: '1.7',
            marginBottom: '1.5rem',
            textAlign: 'justify'
        },

        listItem: {
            fontSize: '1rem',
            color: '#4a5568',
            lineHeight: '1.7',
            marginBottom: '0.5rem',
            textAlign: 'justify'
        },

        content: {
            fontSize: '1rem',
            color: '#2d3748',
            lineHeight: '1.7'
        }
    };

    return (
        <>
            <style>{`
                @media (max-width: 768px) {
                    .container {
                        padding: 0 1rem !important;
                    }
                    
                    .page-title {
                        font-size: 2rem !important;
                        margin-bottom: 2rem !important;
                    }
                    
                    .section-title, .first-section-title {
                        font-size: 1.1rem !important;
                        margin-top: 2rem !important;
                    }
                    
                    .main-content {
                        padding: 2rem 0 !important;
                    }
                }

                @media (max-width: 640px) {
                    .page-title {
                        font-size: 1.8rem !important;
                    }
                    
                    .section-title, .first-section-title {
                        font-size: 1rem !important;
                    }
                    
                    .paragraph {
                        font-size: 0.95rem !important;
                    }
                }
            `}</style>

            <div style={styles.pageContainer}>
                {/* Header */}
                <Header />

                {/* Main Content */}
                <main style={styles.mainContent} className="main-content">
                    <div style={styles.container} className="container">
                        <h1 style={styles.pageTitle} className="page-title">
                            Privacy Policy
                        </h1>

                        <div style={styles.content}>
                            <p style={styles.paragraph}>
                                At WinNest, we are committed to protecting your personal and financial information in accordance with the highest standards of data security and privacy. As an AMFI Registered Mutual Fund Distributor (ARN-81431), we strictly adhere to all applicable regulations including AMFI guidelines, KYC norms, data localization requirements, and the Digital Personal Data Protection Act. This Privacy Policy explains how we collect, use, store, and protect your information when you use our mutual fund distribution platform and services.
                            </p>

                            <p style={styles.paragraph}>
                                This policy is effective from January 1, 2024, and may be updated to reflect changes in regulatory requirements or our practices. We will notify you of any material changes and encourage you to review this policy regularly to stay informed about how we protect your information.
                            </p>

                            <p style={styles.paragraph}>
                                As an AMFI registered mutual fund distributor, WinNest operates under regulatory oversight. We may be required to disclose your information to AMFI, SEBI (for mutual fund related matters), other regulatory authorities, government agencies, judicial bodies, or law enforcement when mandated by applicable laws, regulations, or court orders. Such disclosures are made only as legally required and in compliance with our regulatory obligations.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Information We Collect
                            </h2>
                            <p style={styles.paragraph}>
                                In compliance with KYC (Know Your Customer) norms and regulatory requirements for mutual fund distributors, we collect the following information:
                            </p>
                            <p style={styles.listItem}>
                                <strong>Identity Information:</strong> Full name, date of birth, gender, nationality, and residential address as per officially valid documents
                            </p>
                            <p style={styles.listItem}>
                                <strong>Regulatory Documents:</strong> PAN (Permanent Account Number), Aadhaar number, passport, driving license, or other officially valid identity and address proofs
                            </p>
                            <p style={styles.listItem}>
                                <strong>Financial Information:</strong> Bank account details, income proof, net worth declaration, and investment experience as required for risk profiling
                            </p>
                            <p style={styles.listItem}>
                                <strong>Investment Data:</strong> Transaction history, portfolio holdings, investment preferences, and risk tolerance assessment
                            </p>
                            <p style={styles.listItem}>
                                <strong>Digital Footprint:</strong> Device information, IP addresses, and usage patterns for fraud prevention and system security monitoring
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                How We Use Your Information
                            </h2>
                            <p style={styles.paragraph}>
                                We process your information for the following regulatory and business purposes:
                            </p>
                            <p style={styles.listItem}>
                                <strong>Regulatory Compliance:</strong> Completing KYC verification as mandated for mutual fund distributors, conducting due diligence checks, and maintaining records as required by AMFI and mutual fund regulations
                            </p>
                            <p style={styles.listItem}>
                                <strong>Investment Services:</strong> Processing buy/sell orders, maintaining demat accounts, providing portfolio statements, and facilitating corporate actions
                            </p>
                            <p style={styles.listItem}>
                                <strong>Risk Management:</strong> Monitoring transactions for suspicious activities, conducting periodic risk assessments, and implementing fraud prevention measures
                            </p>
                            <p style={styles.listItem}>
                                <strong>Customer Communication:</strong> Sending regulatory notifications, investment confirmations, market updates, and service-related communications
                            </p>
                            <p style={styles.listItem}>
                                <strong>Service Enhancement:</strong> Analyzing usage patterns to improve our platform functionality and developing new investment products
                            </p>
                            <p style={styles.paragraph}>
                                We do not sell, rent, or lease your personal information to third parties for marketing purposes. Information sharing occurs only when required for service delivery, regulatory compliance, or as mandated by law.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Data Localization & Storage
                            </h2>
                            <p style={styles.paragraph}>
                                In compliance with data localization requirements, all your data and transaction records are stored and processed within the legal boundaries of India. This ensures that regulatory authorities retain their rights of access as required by applicable regulations. Our data storage practices include:
                            </p>
                            <p style={styles.listItem}>
                                Maintaining all customer data, transaction logs, and system records within Indian data centers
                            </p>
                            <p style={styles.listItem}>
                                Ensuring data remains in legible and usable format for regulatory access when required
                            </p>
                            <p style={styles.listItem}>
                                Implementing regular data backups and disaster recovery procedures within India
                            </p>
                            <p style={styles.listItem}>
                                Restricting cross-border data transfers except as permitted under applicable regulations
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Cybersecurity & Data Protection
                            </h2>
                            <p style={styles.paragraph}>
                                In accordance with regulatory requirements and industry best practices, we have implemented comprehensive security measures to protect your information:
                            </p>
                            <p style={styles.listItem}>
                                <strong>Security Operations Center (SOC):</strong> 24/7 monitoring of security events and continuous threat detection
                            </p>
                            <p style={styles.listItem}>
                                <strong>Multi-Factor Authentication (MFA):</strong> Required for all user access to enhance account security
                            </p>
                            <p style={styles.listItem}>
                                <strong>Encryption Standards:</strong> 256-bit encryption for data in transit and at rest, with cryptographic key management
                            </p>
                            <p style={styles.listItem}>
                                <strong>Regular Security Assessments:</strong> Periodic vulnerability assessments and penetration testing (VAPT) as per industry standards
                            </p>
                            <p style={styles.listItem}>
                                <strong>Incident Response:</strong> Established procedures for cybersecurity incident detection, reporting, and response in compliance with CERT-In guidelines
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Third-Party Service Providers
                            </h2>
                            <p style={styles.paragraph}>
                                When we engage third-party service providers for cloud services or other business operations, we ensure they meet regulatory requirements applicable to mutual fund distributors. All service providers must demonstrate compliance with our data localization requirements, security standards, and regulatory obligations. We maintain contractual agreements that protect your data and ensure service providers notify us immediately of any cybersecurity incidents.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Your Privacy Rights
                            </h2>
                            <p style={styles.paragraph}>
                                Under applicable data protection laws and regulations governing mutual fund distributors, you have the right to access, correct, or request deletion of your personal information, subject to our regulatory record-keeping obligations. You can update your information through your account dashboard or by contacting our customer support. Please note that certain information must be retained for regulatory compliance even after account closure.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Data Retention & Compliance
                            </h2>
                            <p style={styles.paragraph}>
                                We retain your information in accordance with AMFI guidelines and other applicable laws. KYC documents and transaction records are maintained for the periods specified by regulatory authorities. Data retention periods vary based on the type of information and regulatory requirements, ensuring we meet our compliance obligations while respecting your privacy rights.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Incident Reporting & Response
                            </h2>
                            <p style={styles.paragraph}>
                                In line with regulatory requirements, we have established robust incident management procedures. Any cybersecurity incidents affecting your data are promptly reported to relevant authorities including CERT-In as required. We maintain detailed incident logs and forensic capabilities to support investigations and ensure transparency in our security practices.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                External Links & Third-Party Websites
                            </h2>
                            <p style={styles.paragraph}>
                                Our platform may contain links to external websites or integrated services for enhanced functionality. These third-party sites operate under their own privacy policies, and we cannot control their data practices. We recommend reviewing the privacy policies of any external sites you visit and exercising caution when sharing personal information.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Contact Information
                            </h2>
                            <p style={styles.paragraph}>
                                For any questions about this Privacy Policy, your data rights, or our data protection practices, please contact our Data Protection Officer or customer support team. We are committed to addressing your privacy concerns promptly and transparently. You may also reach out to AMFI's investor grievance portal if you have concerns about our data handling practices that remain unresolved.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Regulatory Compliance Statement
                            </h2>
                            <p style={styles.paragraph}>
                                This Privacy Policy is designed to comply with all applicable regulations including Know Your Client (KYC) Regulations, AMFI Code of Conduct, Prevention of Money Laundering Act (PMLA) requirements, and the Digital Personal Data Protection Act. Our privacy practices are subject to regular audits and assessments to ensure ongoing compliance with evolving regulatory standards applicable to mutual fund distributors.
                            </p>
                        </div>
                    </div>
                </main>

                {/* Layout */}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default PrivacyPolicyPage;